import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { X, Upload, FileText, AlertCircle, CheckCircle } from "lucide-react";
import { leadUploadService } from "../../services/leadUploadService";
import { useToast } from "../ui/use-toast";

interface FileUploaderProps {
  organizationId: string;
  onUploadComplete?: () => void;
  campaignId?: string;
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "processing" | "complete" | "error";
  error?: string;
}

export function FileUploader({
  organizationId,
  onUploadComplete,
  campaignId,
}: FileUploaderProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const { toast } = useToast();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      console.log(`File drop detected: ${acceptedFiles.length} files`, {
        organizationId,
        campaignId: campaignId || "none",
      });

      // Add files to the uploading list
      const newFiles = acceptedFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 15),
        file,
        progress: 0,
        status: "pending" as const,
      }));

      setUploadingFiles((prev) => [...prev, ...newFiles]);

      // Process each file
      for (const fileObj of newFiles) {
        try {
          console.log(`Processing file: ${fileObj.file.name}`, {
            size: fileObj.file.size,
            type: fileObj.file.type,
          });

          // Update status to uploading
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id ? { ...f, status: "uploading" as const } : f,
            ),
          );

          // Simulate progress updates
          const progressInterval = setInterval(() => {
            setUploadingFiles((prev) =>
              prev.map((f) => {
                if (
                  f.id === fileObj.id &&
                  f.progress < 90 &&
                  f.status === "uploading"
                ) {
                  return { ...f, progress: f.progress + 10 };
                }
                return f;
              }),
            );
          }, 300);

          // Upload the file
          console.log(`Uploading file to Supabase storage...`);
          const { data, error } = await leadUploadService.uploadFile(
            fileObj.file,
            organizationId,
            campaignId,
          );

          clearInterval(progressInterval);

          if (error || !data) {
            console.error("File upload failed:", error);
            throw error || new Error("Upload failed");
          }

          console.log(`File uploaded successfully:`, {
            uploadId: data.id,
            filePath: data.file_path,
            status: data.upload_status,
          });

          // Update status to processing
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, progress: 95, status: "processing" as const }
                : f,
            ),
          );

          // Process the file for data extraction
          console.log(`Processing uploaded file: ${data.id}`);
          const { data: extractedData, error: processError } =
            await leadUploadService.processLeadUpload(data.id);

          if (processError) {
            console.error("Error processing file:", processError);
            throw processError;
          }

          console.log("File processed successfully:", {
            uploadId: data.id,
            status: data.upload_status,
            hasExtractedData: !!extractedData,
          });

          // Update status to complete
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, progress: 100, status: "complete" as const }
                : f,
            ),
          );
        } catch (error) {
          console.error("Error processing file:", error);
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? {
                    ...f,
                    progress: 100,
                    status: "error" as const,
                    error:
                      error instanceof Error ? error.message : "Unknown error",
                  }
                : f,
            ),
          );
        }
      }

      // Notify parent component that uploads are complete
      if (onUploadComplete) {
        console.log("Calling onUploadComplete callback");
        onUploadComplete();
      }
    },
    [organizationId, onUploadComplete, campaignId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "text/plain": [".txt"],
      "application/rtf": [".rtf"],
      "application/vnd.oasis.opendocument.text": [".odt"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const removeFile = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-primary bg-primary/10"
            : "border-gray-300 hover:border-primary/50"
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center space-y-2">
          <Upload className="h-10 w-10 text-gray-400" />
          <p className="text-lg font-medium">
            {isDragActive
              ? "Drop the files here..."
              : "Drag & drop files here, or click to select"}
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF, DOC, DOCX, TXT, RTF, and ODT files up to 10MB
          </p>
        </div>
      </div>

      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-medium">Uploads</h3>
          <div className="space-y-2">
            {uploadingFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center space-x-4 border rounded-md p-3"
              >
                <FileText className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {file.file.name}
                  </p>
                  <div className="mt-1">
                    <Progress value={file.progress} className="h-2" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {file.status === "pending" && "Waiting..."}
                    {file.status === "uploading" && "Uploading..."}
                    {file.status === "processing" && "Processing..."}
                    {file.status === "complete" && "Complete"}
                    {file.status === "error" && (
                      <span className="text-red-500">
                        {file.error || "Error"}
                      </span>
                    )}
                  </p>
                </div>
                {file.status === "complete" && (
                  <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-500" />
                )}
                {file.status === "error" && (
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
                )}
                {(file.status === "pending" ||
                  file.status === "uploading" ||
                  file.status === "processing") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(file.id)}
                    className="flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
